import { useMemo } from 'react';
import {
    Box,
    Flex,
    HStack,
    Table,
    TableContainer,
    Tbody,
    Td,
    Text,
    Tfoot,
    Th,
    Thead,
    Tr,
    VStack,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    Legend,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip as RechartsTooltip,
    XAxis,
    YAxis,
} from 'recharts';
import { Button, Card, Icon, CHART_COLORS, chartColorAt } from '../../ui';
import {
    CarlReportData,
    CarlReportMetricMeta,
    CarlReportStat,
    CarlReportValueFormat,
} from '../../types/askCarl';

/**
 * Reporting info-graphic for Ask Carl (ENG-2169): renders the `ui.report`
 * payload from the carl-agent reporting tools as the same kind of chart +
 * table the Reporting tab shows, inline in the conversation.
 *
 * The agent supplies labels and value formats with the data, so this card has
 * no metric catalog of its own — it just formats and draws.
 */

/** Chart chrome colors via Chakra CSS vars so the card stays token-driven. */
const GRID_STROKE = 'var(--chakra-colors-border-subtle)';
const TICK_FILL = 'var(--chakra-colors-fg-muted)';

const formatValue = (value: number | null | undefined, format: CarlReportValueFormat): string => {
    if (value === null || value === undefined || Number.isNaN(value)) return '—';
    if (format === 'percent') return `${value.toFixed(1)}%`;
    if (format === 'currency') {
        return value.toLocaleString('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: Math.abs(value) < 1000 ? 2 : 0,
        });
    }
    return value.toLocaleString('en-US', { maximumFractionDigits: 1 });
};

/** Short axis labels: $12.5k, 1.2M, 45%. */
const formatAxisValue = (value: number, format: CarlReportValueFormat): string => {
    if (format === 'percent') return `${value}%`;
    const abs = Math.abs(value);
    const compact = abs >= 1_000_000
        ? `${(value / 1_000_000).toFixed(1)}M`
        : abs >= 1_000
            ? `${(value / 1_000).toFixed(abs >= 10_000 ? 0 : 1)}k`
            : `${value}`;
    return format === 'currency' ? `$${compact}` : compact;
};

const formatRangeDate = (dateStr: string): string => {
    const date = new Date(`${dateStr}T00:00:00`);
    if (Number.isNaN(date.getTime())) return dateStr;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const ChartTooltip = ({
    active,
    payload,
    label,
    format,
}: {
    active?: boolean;
    payload?: Array<{ name: string; value: number; color?: string }>;
    label?: string;
    format: CarlReportValueFormat;
}) => {
    if (!active || !payload?.length) return null;
    return (
        <Box bg="white" px={3} py={2} borderRadius="md" border="1px solid" borderColor="border.default">
            {label && (
                <Text fontSize="xs" fontWeight="semibold" color="fg.default" mb={0.5}>
                    {label}
                </Text>
            )}
            {payload.map((entry) => (
                <Text key={entry.name} fontSize="xs" color="fg.muted">
                    {entry.name}: {formatValue(entry.value, format)}
                </Text>
            ))}
        </Box>
    );
};

/** Change vs prior: green/red with direction arrow; points for percent metrics. */
const ChangeIndicator = ({ stat }: { stat: CarlReportStat }) => {
    if (stat.change === null || stat.change === undefined) return null;
    const isUp = stat.change >= 0;
    const suffix = stat.format === 'percent' ? ' pts' : '%';
    return (
        <HStack spacing={0.5}>
            <Icon name={isUp ? 'arrowUp' : 'arrowDown'} size="xs" color={isUp ? 'success.fg' : 'error.fg'} />
            <Text fontSize="xs" color={isUp ? 'success.fg' : 'error.fg'}>
                {Math.abs(stat.change).toFixed(1)}
                {suffix}
            </Text>
        </HStack>
    );
};

const StatsGrid = ({ stats }: { stats: CarlReportStat[] }) => (
    <Flex wrap="wrap" rowGap={4} columnGap={8}>
        {stats.map((stat) => (
            <Box key={stat.key} minW="128px">
                <Text fontSize="xs" color="fg.muted" mb={0.5}>
                    {stat.label}
                </Text>
                <Text fontSize="xl" fontWeight="bold" color="fg.default" lineHeight="1.2">
                    {formatValue(stat.value, stat.format)}
                </Text>
                <ChangeIndicator stat={stat} />
            </Box>
        ))}
    </Flex>
);

const BREAKDOWN_CHART_ROWS = 10;
const BREAKDOWN_ROW_HEIGHT = 30;

const BreakdownChart = ({
    rows,
    metric,
}: {
    rows: Array<{ name: string; value: number | null }>;
    metric: CarlReportMetricMeta;
}) => {
    const data = rows.slice(0, BREAKDOWN_CHART_ROWS).map((row) => ({ ...row, value: row.value ?? 0 }));
    return (
        <Box h={`${Math.max(data.length, 2) * BREAKDOWN_ROW_HEIGHT + 40}px`} w="100%">
            <ResponsiveContainer>
                <BarChart data={data} layout="vertical" margin={{ left: 8, right: 24, top: 4, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} horizontal={false} />
                    <XAxis
                        type="number"
                        tick={{ fontSize: 11, fill: TICK_FILL }}
                        tickFormatter={(value: number) => formatAxisValue(value, metric.format)}
                        stroke={GRID_STROKE}
                    />
                    <YAxis
                        dataKey="name"
                        type="category"
                        width={130}
                        tick={{ fontSize: 11, fill: TICK_FILL }}
                        tickLine={false}
                        stroke={GRID_STROKE}
                    />
                    <RechartsTooltip content={<ChartTooltip format={metric.format} />} cursor={{ fill: 'var(--chakra-colors-bg-subtle)' }} />
                    <Bar dataKey="value" name={metric.label} radius={[0, 4, 4, 0]} maxBarSize={18}>
                        {data.map((_, index) => (
                            <Cell key={index} fill={CHART_COLORS[0]} />
                        ))}
                    </Bar>
                </BarChart>
            </ResponsiveContainer>
        </Box>
    );
};

const TrendChart = ({
    series,
    metric,
}: {
    series: NonNullable<CarlReportData['series']>;
    metric: CarlReportMetricMeta;
}) => {
    // Recharts wants one row per date with a column per series.
    const chartData = useMemo(() => {
        const byDate = new Map<string, Record<string, string | number | null>>();
        for (const entry of series) {
            for (const point of entry.points) {
                if (!byDate.has(point.date)) byDate.set(point.date, { date: point.date });
                byDate.get(point.date)![entry.name] = point.value;
            }
        }
        return Array.from(byDate.values()).sort((a, b) => String(a.date).localeCompare(String(b.date)));
    }, [series]);

    return (
        <Box h="260px" w="100%">
            <ResponsiveContainer>
                <LineChart data={chartData} margin={{ left: 8, right: 16, top: 8, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={GRID_STROKE} vertical={false} />
                    <XAxis
                        dataKey="date"
                        tick={{ fontSize: 11, fill: TICK_FILL }}
                        tickFormatter={formatRangeDate}
                        stroke={GRID_STROKE}
                    />
                    <YAxis
                        tick={{ fontSize: 11, fill: TICK_FILL }}
                        tickFormatter={(value: number) => formatAxisValue(value, metric.format)}
                        stroke={GRID_STROKE}
                        width={56}
                    />
                    <RechartsTooltip
                        content={<ChartTooltip format={metric.format} />}
                        labelFormatter={formatRangeDate}
                    />
                    {series.length > 1 && <Legend wrapperStyle={{ fontSize: 12 }} />}
                    {series.map((entry, index) => (
                        <Line
                            key={entry.name}
                            type="monotone"
                            dataKey={entry.name}
                            name={entry.name}
                            stroke={chartColorAt(index)}
                            strokeWidth={2}
                            dot={{ r: 2.5 }}
                            activeDot={{ r: 4.5 }}
                            connectNulls
                        />
                    ))}
                </LineChart>
            </ResponsiveContainer>
        </Box>
    );
};

const BreakdownTable = ({
    domainLabel,
    metrics,
    rows,
    totals,
}: {
    domainLabel: string;
    metrics: CarlReportMetricMeta[];
    rows: NonNullable<CarlReportData['rows']>;
    totals?: Array<number | null>;
}) => (
    <TableContainer border="1px solid" borderColor="border.subtle" borderRadius="lg">
        <Table size="sm" variant="simple">
            <Thead>
                <Tr>
                    <Th>{domainLabel}</Th>
                    {metrics.map((metric) => (
                        <Th key={metric.key} isNumeric>
                            {metric.label}
                        </Th>
                    ))}
                </Tr>
            </Thead>
            <Tbody>
                {rows.map((row) => (
                    <Tr key={row.id}>
                        <Td>
                            <Text fontSize="xs" fontWeight="medium" color="fg.default" noOfLines={1}>
                                {row.name}
                            </Text>
                        </Td>
                        {metrics.map((metric, metricIndex) => (
                            <Td key={metric.key} isNumeric>
                                <Text fontSize="xs" color="fg.default">
                                    {formatValue(row.values[metricIndex], metric.format)}
                                </Text>
                            </Td>
                        ))}
                    </Tr>
                ))}
            </Tbody>
            {totals && totals.some((value) => value !== null && value !== undefined) && (
                <Tfoot>
                    <Tr>
                        <Td>
                            <Text fontSize="xs" fontWeight="semibold" color="fg.default">
                                Total
                            </Text>
                        </Td>
                        {metrics.map((metric, metricIndex) => (
                            <Td key={metric.key} isNumeric>
                                <Text fontSize="xs" fontWeight="semibold" color="fg.default">
                                    {formatValue(totals[metricIndex], metric.format)}
                                </Text>
                            </Td>
                        ))}
                    </Tr>
                </Tfoot>
            )}
        </Table>
    </TableContainer>
);

const DOMAIN_LABELS: Record<string, string> = {
    location: 'Location',
    vendor: 'Vendor',
    insurance: 'Insurance',
    oem: 'OEM',
    user: 'User',
};

export const CarlReportingCard = ({ report }: { report: CarlReportData }) => {
    const navigate = useNavigate();
    const rangeLabel = report.dateRange.label
        ?? `${formatRangeDate(report.dateRange.startDate)} – ${formatRangeDate(report.dateRange.endDate)}`;

    const primaryMetric = report.metrics?.[0] ?? report.metric;
    const chartRows = useMemo(
        () => (report.rows ?? []).map((row) => ({ name: row.name, value: row.values[0] ?? null })),
        [report.rows],
    );

    return (
        <Card variant="outline" bg="white" px={4} py={3.5}>
            <VStack align="stretch" spacing={3}>
                <Flex justify="space-between" align="flex-start" gap={2}>
                    <Box>
                        <Text fontSize="sm" fontWeight="semibold" color="fg.default">
                            {report.title}
                        </Text>
                        <Text fontSize="xs" color="fg.muted">
                            {rangeLabel}
                            {report.kind === 'trend' && report.granularity ? ` · by ${report.granularity}` : ''}
                        </Text>
                    </Box>
                    <Button
                        variant="link"
                        size="sm"
                        rightIcon={<Icon name="externalLink" size="sm" />}
                        flexShrink={0}
                        onClick={() => navigate('/reporting')}
                    >
                        Open Reporting
                    </Button>
                </Flex>

                {(report.kind === 'overview' || report.kind === 'invoicing') && report.stats?.length ? (
                    <StatsGrid stats={report.stats} />
                ) : null}

                {report.kind === 'breakdown' && primaryMetric && chartRows.length > 0 && (
                    <BreakdownChart rows={chartRows} metric={primaryMetric} />
                )}
                {report.kind === 'breakdown' && report.metrics?.length && report.rows?.length ? (
                    <BreakdownTable
                        domainLabel={DOMAIN_LABELS[report.domain ?? ''] ?? 'Name'}
                        metrics={report.metrics}
                        rows={report.rows}
                        totals={report.totals}
                    />
                ) : null}

                {report.kind === 'trend' && report.metric && report.series?.length ? (
                    <TrendChart series={report.series} metric={report.metric} />
                ) : null}
            </VStack>
        </Card>
    );
};

export default CarlReportingCard;
